import { useMemo } from 'react';
import { match } from 'ts-pattern';
import { Creator } from '~/clientApi/creator.model';
import {
  FEATURE_FLAGS,
  PARTIALLY_SUPPORTED_PLATFORMS,
  SOCIAL_PLATFORMS,
} from '~/constants';
import { DEMOGRAPHICS_SOURCES } from '~/constants/demographicsSources';
import {
  BASE_SUPPORTED_PLATFORMS,
  SOCIAL_PLATFORMS_CONNECTION_STATUS,
  SupportedPlatforms,
} from '~/constants/platforms';
import type { IGInsights } from '~/hooks/api/insights/useGetIGInsights';
import type { TTInsights } from '~/hooks/api/insights/useGetTTInsights';
import { useFeature } from '~/hooks/useFeatureFlag/useFeature';
import { useInsights } from '~/screens/CreatorAudienceDemographics/GenderDistribution/useInsights';
import {
  getInsightId,
  getIsEmptyData,
} from '~/screens/CreatorAudienceDemographics/utils';
import { Platform } from '~/screens/types';

interface usePlatformMetricsProps {
  creator?: Creator;
  platform?: SupportedPlatforms;
}

export const usePlatformMetrics = ({
  creator,
  platform,
}: usePlatformMetricsProps) => {
  const foamSnapchatAnalyticsFlag = useFeature(
    FEATURE_FLAGS.foamSnapchatAnalytics,
  );
  const foamModashAudienceDemographicsFlag = useFeature(
    FEATURE_FLAGS.foamModashAudienceDemographics,
  );

  const insightId = useMemo(() => {
    if (!creator || !platform) {
      return null;
    }
    return getInsightId({ creator, platform });
  }, [creator, platform]);

  const insights = useInsights({
    insightId: insightId ?? '',
    platform: platform as Platform,
  });

  const insightsByType = useMemo(() => {
    if (!insights.data) {
      return null;
    }

    return {
      GENDERS: insights.data.genders ?? [],
      AGES: insights.data.ages ?? [],
      COUNTRIES: insights.data.countries ?? [],
    };
  }, [insights.data]);

  const connectionStatus = useMemo(() => {
    if (!creator || !platform) {
      return;
    }

    return creator.platforms[platform]?.status;
  }, [creator, platform]);

  const isVerifiedCreator = useMemo(() => {
    if (!creator || !platform) {
      return false;
    }

    return (
      connectionStatus === SOCIAL_PLATFORMS_CONNECTION_STATUS.disconnected ||
      connectionStatus === SOCIAL_PLATFORMS_CONNECTION_STATUS.connected
    );
  }, [creator, platform]);

  const isPlatformConnected = useMemo(() => {
    if (!creator || !platform) {
      return false;
    }

    return match(platform)
      .with(PARTIALLY_SUPPORTED_PLATFORMS.snapchat, () => {
        return (
          connectionStatus === SOCIAL_PLATFORMS_CONNECTION_STATUS.connected ||
          connectionStatus ===
            SOCIAL_PLATFORMS_CONNECTION_STATUS.disconnected ||
          connectionStatus === SOCIAL_PLATFORMS_CONNECTION_STATUS.offPlatform
        );
      })
      .otherwise(() => {
        return (
          connectionStatus === SOCIAL_PLATFORMS_CONNECTION_STATUS.connected ||
          connectionStatus === SOCIAL_PLATFORMS_CONNECTION_STATUS.disconnected
        );
      });
  }, [creator, platform, connectionStatus]);

  const demographicSource = useMemo(() => {
    if (!creator || !platform) {
      return null;
    }

    const insightsData = insights.data as IGInsights | TTInsights | undefined;
    const creatorYoutubeChannel = creator.ytChannels?.[0];

    return match(platform)
      .with(
        SOCIAL_PLATFORMS.youtube,
        () => creatorYoutubeChannel?.demographicsSource,
      )
      .otherwise(() => insightsData?.demographicsSource);
  }, [creator, platform, insights.data]);

  const allDemographicsEmpty = useMemo(() => {
    if (!insightsByType) {
      return false;
    }

    return (
      isEmptyOrUndefined(insightsByType.GENDERS) &&
      isEmptyOrUndefined(insightsByType.AGES) &&
      isEmptyOrUndefined(insightsByType.COUNTRIES)
    );
  }, [insightsByType]);

  const isSTMPrediction = useMemo(() => {
    if (!demographicSource) {
      return false;
    }
    return (
      demographicSource === DEMOGRAPHICS_SOURCES.modashEstimated &&
      foamModashAudienceDemographicsFlag.isEnabled
    );
  }, [demographicSource, foamModashAudienceDemographicsFlag.isEnabled]);

  const isSTMPredictedVisible = useMemo(() => {
    return (
      connectionStatus === SOCIAL_PLATFORMS_CONNECTION_STATUS.offPlatform &&
      isSTMPrediction
    );
  }, [connectionStatus, isSTMPrediction]);

  /**
   * Off-platform account whose demographics come from TikTok's public data.
   * Not a Modash estimate, so it stays out of `isSTMPrediction` and its
   * "Indicative" copy, but it is just as renderable (FOAM-23261).
   */
  const isPublicDemographicsVisible = useMemo(() => {
    return (
      connectionStatus === SOCIAL_PLATFORMS_CONNECTION_STATUS.offPlatform &&
      demographicSource === DEMOGRAPHICS_SOURCES.tiktokPublic
    );
  }, [connectionStatus, demographicSource]);

  /** Not connected, but the API still sent demographics we can render. */
  const hasOffPlatformDemographics =
    isSTMPredictedVisible || isPublicDemographicsVisible;

  const isEmpty = useMemo(() => {
    if (!insights.data) {
      return false;
    }
    return getIsEmptyData(insights.data);
  }, [insights.data]);

  /**
   * The account genuinely has no usable connection, so asking the viewer to
   * connect it is the correct thing to show.
   */
  const showConnectPrompt = useMemo(() => {
    return !isPlatformConnected && !hasOffPlatformDemographics;
  }, [isPlatformConnected, hasOffPlatformDemographics]);

  /**
   * The account IS connected, but the audience-demographics payload came back
   * with no genders/ages/countries. TikTok reaches this constantly — the
   * insight record exists while the demographics stay unavailable.
   *
   * This says nothing about the connection, and nothing about channel metrics,
   * which come from a different endpoint entirely (FOAM-23134).
   */
  const hasNoDemographics = useMemo(() => {
    return isEmpty && !isSTMPredictedVisible;
  }, [isEmpty, isSTMPredictedVisible]);

  /**
   * @deprecated Conflates "not connected" with "no demographics". Prefer
   * `showConnectPrompt` / `hasNoDemographics` — picking the wrong one of the
   * two is what hid verified creators' channel metrics behind a "connect this
   * account" prompt (FOAM-23134).
   */
  const showEmptyState = useMemo(() => {
    return showConnectPrompt || hasNoDemographics;
  }, [showConnectPrompt, hasNoDemographics]);

  const platformsToShow = useMemo((): SupportedPlatforms[] => {
    if (foamSnapchatAnalyticsFlag.isEnabled) {
      return [
        ...BASE_SUPPORTED_PLATFORMS,
        PARTIALLY_SUPPORTED_PLATFORMS.snapchat,
      ];
    }
    return [...BASE_SUPPORTED_PLATFORMS];
  }, [foamSnapchatAnalyticsFlag.isEnabled]);

  return {
    connectionStatus,
    isVerifiedCreator,
    isPlatformConnected,
    isEmpty,
    showEmptyState,
    showConnectPrompt,
    hasNoDemographics,
    demographicSource,
    isSTMPrediction,
    isSTMPredictedVisible,
    isPublicDemographicsVisible,
    hasOffPlatformDemographics,
    insights: {
      isLoading: insights.isLoading,
      insightId: insightId ?? '',
      data: insights.data,
      byType: insightsByType,
    },
    platformDemographics: {
      source: demographicSource,
      allAreEmpty: allDemographicsEmpty,
    },
    platformsToShow,
  };
};

// Helper to check if data is undefined or empty
export function isEmptyOrUndefined(data: unknown[] | undefined) {
  return data === undefined || data.length === 0;
}
